import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouteLink } from 'react-router-dom';
import {
    Button,
    Divider,
    Drawer,
    Hidden,
    Icon,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import classnames from 'classnames';

import { AuthRoutes } from '@klpkonsern/auth-core/routes';
import { useCurrentUser } from '@klp/auth';
import { useGetUserClientsQuery } from '@klp/client/components/Select/rtkApi';
import { AppRoutes } from '@klp/core/app/config';
import { useAppParams } from '@klp/core/app/useAppParams';
import { Block } from '@klp/design/Block';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Link } from '@klp/design/Link';
import { Logo } from '@klp/design/Logo';
import { useKlpStyles } from '@klp/design/Theme';
import { KlpFontVariant } from '@klp/design/Theme/KlpFontVariant';
import { useLayoutContext } from '@klp/layout/context/LayoutContext';
import { translationsObject } from '@klp/translations';
import { ParamType, getFromStorage } from '@klp/utils';

import type { SidebarMenuItem } from './classes/SidebarMenuItem';
import { getDefaultLanguage, IdentityServerURL } from './config';
import type { ISidebarOwnProps } from './interfaces';
import { NestedSidebarMenuItem } from './NestedSidebarMenuItem';

const applicationBarStyles = (theme: Theme) => ({
    drawer: {
        '& > *': {
            transition: 'width 0.25s cubic-bezier(0.67, 0, 0.33, 1)',
            width: theme.menu.width + 1,
        },
        '& .MuiListItem-root': {
            height: 51,
            overflow: 'hidden',
            '&:hover': {
                backgroundColor: theme.palette.secondary.veryLight,
            },
            '&.Mui-selected': {
                color: theme.palette.background.default,
                backgroundColor: theme.palette.secondary.main,
                '& svg': {
                    fill: theme.palette.background.default,
                },
            },
        },
        '& .MuiListItem-root.noHover': {
            '&:hover': {
                backgroundColor: 'inherit',
            },
        },
        '& .MuiListItemIcon-root': {
            minWidth: 40,
            justifyContent: 'center',
        },
        '& .MuiListItemText-root': {
            whiteSpace: 'nowrap',
            paddingLeft: '0.5em',
            opacity: 0,
            transition: 'opacity 0.15s linear',
        },
        '& .MuiIconButton-root': {
            opacity: 1,
            transition: 'opacity 0.25s linear',
            marginLeft: 0,
        },
    },
    drawerOpen: {
        '& > *': {
            width: theme.menuOpen.width,
        },
        '& .MuiListItemText-root': {
            opacity: 1,
        },
    },
    drawerClosed: {
        '& .MuiIconButton-root': {
            opacity: 0,
        },
    },
    drawerMobile: {
        '& > *': {
            width: '100%',
        },
    },
    logo: {
        height: '64px',
        fill: '#000',
        transition: 'fill 0.25s linear, height 0.25s linear',
        width: '56px',
        '&:before': {
            display: 'none',
        },
        '& svg': {
            height: '52px',
            width: '56px',
        },
    },
    drawerLogo: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: theme.topbar.height,
        padding: '0 8px',
        position: 'fixed',
        zIndex: 1100,
        background: theme.palette.background.default,
        width: theme.menu.width,
        transition: 'width 0.25s cubic-bezier(0.67, 0, 0.33, 1)',
        '& .Sidebar__Menu--pin': {
            left: -10000,
            opacity: 0,
            transition:
                'opacity 0.25s cubic-bezier(0.67, 0, 0.33, 1), left 0.25s linear',
        },
    },
    drawerLogoOpen: {
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            width: `${theme.menuOpen.width - 1}px`,
        },
        '& .Sidebar__Menu--pin': {
            left: 0,
            opacity: 1,
        },
    },
    centered: {
        justifyContent: 'center',
    },
    languageLinks: {
        '& span': {
            paddingLeft: '0.1em',
            paddingRight: '0.1em',
        },
        '& span:first-of-type': {
            paddingLeft: 0,
        },
        '& a': {
            color: theme.palette.primary.main,
        },
        '& a.active': {
            color: theme.palette.secondary.main,
        },
    },
    menuItems: {
        paddingTop: theme.topbar.height,
    },
    pin: {
        '& .MuiIcon-root': {
            fontSize: KlpFontVariant.default.fontSize,
        },
    },
    logout: {
        borderRadius: theme.spacing(0.2),
    },
});

export type ISidebarProps = ISidebarOwnProps;

export const Sidebar: React.FC<ISidebarProps> = ({
    menuItems,
    sustainabilityMenuItems,
    secondaryMenuItems,
}) => {
    const { t, i18n } = useTranslation();
    const classes = useKlpStyles({}, applicationBarStyles);
    const params = useAppParams();
    const language = i18n.language;

    const { currentUser: user, isAuthenticated } = useCurrentUser();

    const { menuOpen, menuPin, toggleMenu, setMenuPin } = useLayoutContext();
    const { selectedClient } = useAppParams();

    const { data: clients = [] } = useGetUserClientsQuery(
        { includeInActive: true },
        { skip: !isAuthenticated || !user },
    );

    const locale = i18n.language;

    const handleDrawerClose = () => toggleMenu(false);

    const handleDrawerOpen = () => toggleMenu(true);

    const handleMenuPin = () => setMenuPin(!getFromStorage(ParamType.menuPin));

    const handleLanguageSelect = (lang: string) => () => {
        i18n.changeLanguage(lang);
        localStorage.setItem('lang', lang);
    };

    const renderLanguageLinks = () => {
        const languages = Object.keys(translationsObject);

        return (
            <ListItem className="noHover">
                <ListItemIcon>
                    <Illustration
                        icon={IconName.GLOBE}
                        klpColor="main"
                        size="medium"
                    />
                </ListItemIcon>
                <ListItemText
                    className={classes.languageLinks}
                    primary={languages.map((languageCode: string) => (
                        <Fragment key={languageCode}>
                            <Link
                                className={
                                    locale === languageCode ? 'active' : ''
                                }
                                href="#"
                                onClick={handleLanguageSelect(languageCode)}
                            >
                                {
                                    translationsObject[languageCode][
                                        'languageName'
                                    ]
                                }
                            </Link>
                            {languageCode !==
                                languages[languages.length - 1] && (
                                <span> ▪ </span>
                            )}
                        </Fragment>
                    ))}
                />
            </ListItem>
        );
    };

    const renderWithAccess = (
        element: SidebarMenuItem,
        key: string | undefined,
    ) =>
        element.access &&
        element.access(user) &&
        renderMenuItem(element, key || '');

    const renderWithCondition = (
        element: SidebarMenuItem,
        key: string | undefined,
    ) =>
        element?.condition &&
        element.condition({ user, params, selectedClient, clients }) &&
        renderMenuItem(element, key || '');

    const renderMenuItem = (element: SidebarMenuItem, key: string) => {
        const { matchRoute } = params;
        return (
            <Fragment key={key}>
                {element.divider && (
                    <Block marginVertical="compact">
                        <Divider />
                    </Block>
                )}
                {!element.divider && (
                    <ListItem
                        button={true}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        {...({
                            component: RouteLink,
                            to: element.route,
                        } as any)}
                        selected={matchRoute(element.matchRoute || '')}
                        onClick={() => {
                            if (element?.callback) {
                                element.callback({
                                    byggnummer: '',
                                    kundenummer:
                                        selectedClient?.kundeNummer ?? '',
                                });
                            }
                            handleDrawerClose();
                        }}
                    >
                        <ListItemIcon>
                            {element.pictogram && (
                                <Illustration pictogram={element.pictogram} />
                            )}
                            {element.icon && (
                                <Illustration
                                    icon={element.icon}
                                    klpColor="main"
                                    klpPalette="primary"
                                />
                            )}
                        </ListItemIcon>
                        <ListItemText primary={t(element.translation || '')} />
                    </ListItem>
                )}
            </Fragment>
        );
    };

    const renderMenuItems = () => {
        return (
            <List component="nav" className={classes.menuItems}>
                {menuItems.map((element, index) => {
                    const key =
                        element.route || element.translation || `menu-${index}`;
                    if (element.access) {
                        return renderWithAccess(element, key);
                    }
                    if (element.condition) {
                        return renderWithCondition(element, key);
                    }
                    return null;
                })}
                <NestedSidebarMenuItem
                    icon={IconName.PLANT}
                    LinkComponent={RouteLink}
                    translation="frontPage.miljo.title"
                    drawerCallback={handleDrawerClose}
                    routeHighlight={params.matchRoute}
                    nestedItems={sustainabilityMenuItems}
                    user={user}
                />
                {secondaryMenuItems.map((element, index) => {
                    const key =
                        element.route ||
                        element.translation ||
                        `secondary-${index}`;
                    if (element.access) {
                        return renderWithAccess(element, key);
                    }
                    if (element.condition) {
                        return renderWithCondition(element, key);
                    }
                    return null;
                })}
            </List>
        );
    };

    const renderDrawer = () => {
        return (
            <div>
                <div
                    className={classnames(classes.drawerLogo, {
                        [classes.drawerLogoOpen]: menuOpen,
                    })}
                >
                    <Link className={classes.logo} href={AppRoutes.mainPage()}>
                        <Logo />
                    </Link>
                    <Hidden xsDown={true}>
                        <IconButton
                            className={classnames(
                                'Sidebar__Menu--pin',
                                classes.pin,
                            )}
                            onClick={handleMenuPin}
                            color={menuPin ? 'secondary' : 'primary'}
                        >
                            <Icon>push_pin</Icon>
                        </IconButton>
                    </Hidden>

                    <Hidden smUp={true}>
                        <IconButton
                            className="Sidebar__Menu--close"
                            onClick={handleDrawerClose}
                        >
                            <Icon>close</Icon>
                        </IconButton>
                    </Hidden>
                </div>

                {renderMenuItems()}

                <List component="nav">
                    <Divider />
                    {renderLanguageLinks()}

                    {user && (
                        <Block marginBottom="compact">
                            <ListItem className="noHover">
                                <ListItemIcon>
                                    <Illustration
                                        icon={IconName.PROFILE_FILLED_UKR}
                                        klpColor="main"
                                    />
                                </ListItemIcon>
                                <ListItemText>
                                    {user.firstName} {user.lastName}
                                </ListItemText>
                            </ListItem>
                            <ListItem className="noHover">
                                <ListItemIcon>
                                    <Icon />
                                </ListItemIcon>
                                <ListItemText>
                                    <Link
                                        href={
                                            IdentityServerURL() +
                                            (language !== null &&
                                            language !== undefined
                                                ? language
                                                : getDefaultLanguage())
                                        }
                                        translation="frontPage.accountSettings"
                                    />
                                </ListItemText>
                            </ListItem>
                            {/* <ListItem className="noHover">
                                <ListItemIcon>
                                    <Icon />
                                </ListItemIcon>
                                <ListItemText>{user.email}</ListItemText>
                            </ListItem> */}
                            <ListItem
                                className={classnames(
                                    'noHover',
                                    classes.centered,
                                )}
                            >
                                <ListItemIcon>
                                    <Icon />
                                </ListItemIcon>
                                <ListItemText>
                                    <Button
                                        className={classes.logout}
                                        href={AuthRoutes.logout}
                                        startIcon={
                                            <Illustration
                                                icon={IconName.LOGOUT}
                                                klpColor="main"
                                            />
                                        }
                                        variant="outlined"
                                    >
                                        {t('auth.logout')}
                                    </Button>
                                </ListItemText>
                            </ListItem>
                        </Block>
                    )}
                </List>
            </div>
        );
    };

    const drawerClass = menuOpen ? 'drawerOpen' : 'drawerClosed';

    return isAuthenticated && user ? (
        <>
            <Hidden xsDown={true} implementation="js">
                <Drawer
                    className={classnames(classes.drawer, {
                        [classes.drawerOpen]: menuOpen,
                        [classes.drawerClosed]: !menuOpen,
                    })}
                    variant="persistent"
                    anchor="left"
                    open={true}
                    onMouseEnter={handleDrawerOpen}
                    onMouseLeave={handleDrawerClose}
                    transitionDuration={250}
                    ModalProps={{
                        onBackdropClick: handleDrawerClose,
                    }}
                >
                    {renderDrawer()}
                </Drawer>
            </Hidden>
            <Hidden smUp={true} implementation="js">
                <Drawer
                    className={classnames(
                        classes.drawer,
                        classes[drawerClass],
                        classes.drawerMobile,
                    )}
                    variant="temporary"
                    anchor="left"
                    open={menuOpen}
                    transitionDuration={250}
                    ModalProps={{
                        onBackdropClick: handleDrawerClose,
                    }}
                >
                    {renderDrawer()}
                </Drawer>
            </Hidden>
        </>
    ) : null;
};

Sidebar.displayName = 'Sidebar';
